<script setup>
    import { useAuthStore } from "../../utils/authStore";
    import { fetchWithAuth } from "../../utils/authFetch";
    import { storeToRefs } from "pinia";
    import * as signalR from "@microsoft/signalr";

</script>
<template>
    <div v-if="paymentSent" class="order-tracker">
        <h2>Order Progress</h2>
        <div v-if="barStarted">
            <div class="circular-progress-wrapper">
                <svg class="circular-progress" viewBox="0 0 110 110">
                    <!-- Background arcs (always visible) -->
                    <circle v-for="(arc, i) in arcSegments"
                            :key="'bg-' + i"
                            class="arc-background arc-fill"
                            :class="{ active: arc.isCurrent }"
                            cx="55"
                            cy="55"
                            r="42"
                            :stroke="arc.color"
                            stroke-opacity="0.2"
                            stroke-width="10"
                            fill="none"
                            :stroke-dasharray="arc.length + ', ' + (2 * Math.PI * 42)"
                            :stroke-dashoffset="arc.offset" />


                    <!-- Foreground arcs (progress fills based on stage color) -->
                    <circle v-for="(arc, i) in arcSegments"
                            :key="'fill-' + i"
                            class="arc-fill"
                            :class="{ active: arc.isCurrent }"
                            cx="55"
                            cy="55"
                            r="42"
                            :stroke="arc.progress > 0 ? arc.progressColor : 'transparent'"
                            stroke-width="10"
                            fill="none"
                            :stroke-dasharray="(arc.progress * arc.length).toFixed(2) + ', ' + (2 * Math.PI * 42)"
                            :stroke-dashoffset="arc.offset" />
                    <circle fill="white" r="36.8"
                            cx="55"
                            cy="55" />
                </svg>
                <div class="icon-wrapper">
                    <img :src="currentIcon" alt="status icon" />
                </div>
            </div>

            <!--<div class="progress-bar">
              <div class="progress" :style="{ width: `${progressPercent}%` }"></div>
              <img src="/assets/pizza-icon.svg" class="tracker-icon" :style="{ left: `calc(${progressPercent}% - 12px)` }" />
            </div>-->

            <ul class="stages">
                <li v-for="(stage, index) in stages"
                    :key="index"
                    :class="{ active: index < currentStage, current: index === currentStage }">
                    <span class="stage-title">
                        {{ collectionMode && stage.status === 'ready_for_delivery' ? 'Ready to Collect' : stage.label }}
                    </span>
                    <span class="stage-time">
                        {{ index === currentStage ? formatRemainingTime(stage.remaining) : '' }}
                    </span>
                </li>
            </ul>
        </div>
        <div v-if="!barStarted"><p>Waiting for vendor to accept</p></div>
    </div>
</template>

<script>
    import { mapState } from 'pinia';
    export default {
        props: {
            orderId: Number,
            paymentSent: Boolean,
            vendorId: Number
        },
        data() {
            return {
                connection: null,
                currentStage: 0,
                progressPercent: 0,
                elapsedTime: 0,
                interval: null,
                iconInterval: null,
                barStarted: false,
                currentIconUrl: '',
                collectionMode: false,
                stages: [
                    {
                        status: "confirmed",
                        label: "Waiting for Confirmation",
                        duration: 5,
                        remaining: 5,
                        color: "#3498db",
                        progressColor: "#2980b9",
                        icon: "/assets/tracker/confirm.gif"
                    },
                    {
                        status: "preparing",
                        label: "Preparing Food",
                        duration: 10,
                        remaining: 10,
                        color: "#f39c12",
                        progressColor: "#e67e22",
                        icon: "/assets/tracker/prep1.gif,/assets/tracker/prep2.gif,/assets/tracker/prep3.gif,/assets/tracker/prep4.gif"
                    },
                    {
                        status: "in_oven",
                        label: "In the Oven",
                        duration: 30,
                        remaining: 30,
                        color: "#e74c3c",
                        progressColor: "#c0392b",
                        icon: "/assets/tracker/cook1.gif,/assets/tracker/cook2.gif,/assets/tracker/cook3.gif,/assets/tracker/cook4.gif"
                    },
                    {
                        status: "out_for_delivery",
                        label: "Out for Delivery",
                        duration: 30,
                        remaining: 30,
                        color: "#8e44ad",
                        progressColor: "#7d3c98",
                        icon: "/assets/tracker/delivery1.gif,/assets/tracker/delivery2.gif"
                    },
                    {
                        status: "completed",
                        label: "Delivered",
                        duration: 5,
                        remaining: 5,
                        color: "#045f4d",
                        progressColor: "#045f4d",
                        icon: "/assets/tracker/confirm.gif"
                    }
                ]
            };
        },
        watch: {
            vendorId: {
                immediate: true,
                async handler() {
                    await this.loadDurations();
                }
            },
            user(newUser, oldUser) {
                if (newUser && !oldUser) {
                    this.setupSignalR();
                }
            }
        },
        computed: {
            ...mapState(useAuthStore, ['user']),
            isAdmin() {
                return this.user?.roles?.includes("Admin");
            },
            totalTime() {
                return this.stages.reduce((sum, stage) => sum + stage.duration, 0);
            },
            currentIcon() {
                return this.currentIconUrl || this.stages[this.currentStage]?.icon?.split(',')[0] || "";
            },
            arcSegments() {
                const total = this.totalTime;
                let accOffset = 0;
                const fullCircle = 2 * Math.PI * 45;
                return this.stages.map((stage, index) => {
                    const weight = stage.duration / total;
                    const length = weight * fullCircle;
                    const stageStart = this.stages.slice(0, index).reduce((sum, s) => sum + s.duration, 0);
                    const stageEnd = stageStart + stage.duration;
                    let progress = 0;
                    if (this.elapsedTime >= stageEnd) {
                        progress = 1;
                    } else if (this.elapsedTime >= stageStart) {
                        progress = (this.elapsedTime - stageStart) / stage.duration;
                    }
                    const arc = {
                        color: stage.color,
                        progressColor: stage.progressColor,
                        length,
                        offset: -accOffset,
                        progress,
                        isCurrent: index === this.currentStage
                    };
                    accOffset += length;
                    return arc;
                });
            }
        },
        async mounted() {

            await this.checkOrderStatus();
            this.setupSignalR();
            this.startIconRotation();
            this.startProgress();
        },
        beforeUnmount() {
            if (this.interval) clearInterval(this.interval);
            if (this.iconInterval) clearTimeout(this.iconInterval);
        },
        methods: {
            startIconRotation() {
                const update = () => {
                    const stage = this.stages[this.currentStage];
                    const icons = stage.icon?.split(',') || [];
                    if (icons.length > 1) {
                        const next = icons[Math.floor(Math.random() * icons.length)];
                        this.currentIconUrl = next.trim();
                    } else {
                        this.currentIconUrl = icons[0] || '';
                    }
                    const nextDelay = Math.floor(10000 + Math.random() * 20000);
                    this.iconInterval = setTimeout(update, nextDelay);
                };
                update();
            },
            startProgress() {
                if (this.barStarted) {
                    return;
                }
                console.log("start");
                this.barStarted = true;
                this.interval = setInterval(() => {
                    this.elapsedTime++;
                    this.updateStageProgress();
                    this.updateProgressBar();
                }, 1000);
            },
            updateStageProgress() {
                let elapsed = this.elapsedTime;
                for (let i = 0; i < this.stages.length; i++) {
                    const stageStart = this.stages.slice(0, i).reduce((sum, s) => sum + s.duration, 0);
                    const stageEnd = stageStart + this.stages[i].duration;
                    if (elapsed < stageEnd) {
                        this.currentStage = i;
                        this.stages[i].remaining = stageEnd - elapsed;
                        for (let j = i + 1; j < this.stages.length; j++) {
                            this.stages[j].remaining = this.stages[j].duration;
                        }
                        break;
                    } else {
                        this.stages[i].remaining = 0;
                    }
                }
            },
            updateProgressBar() {
                let progress = 0;
                let elapsed = this.elapsedTime;
                for (let i = 0; i < this.stages.length; i++) {
                    if (i < this.currentStage) {
                        progress += this.stages[i].duration;
                    } else if (i === this.currentStage) {
                        const stageStart = this.stages.slice(0, i).reduce((sum, s) => sum + s.duration, 0);
                        progress += elapsed - stageStart;
                        break;
                    }
                }
                this.progressPercent = Math.min((progress / this.totalTime) * 100, 100);
            },
            formatRemainingTime(seconds) {
                const m = Math.floor(seconds / 60);
                const s = seconds % 60;
                return `${m}m ${s < 10 ? "0" : ""}${s}s`;
            },
            setupSignalR() {
                const baseUrl = import.meta.env.PROD
                    ? 'https://hub.pittroche.co.uk'
                    : 'https://localhost:7113';
                this.connection = new signalR.HubConnectionBuilder()
                    .withUrl(baseUrl + '/orderHub', {
                        accessTokenFactory: () => localStorage.getItem("jwt"),
                    })
                    .withAutomaticReconnect()
                    .build();

                this.connection.on("SendOrderUpdate_" + this.orderId, (status) => {
                    console.log(status, this.orderId);
                    const stageIndex = this.stages.findIndex((s) =>
                        s.status.toLowerCase().includes(status.toLowerCase())
                    );
                    console.log(this.stages)
                    console.log(stageIndex)

                    if (stageIndex !== -1) {
                        this.startProgress();
                        this.currentStage = stageIndex;
                        this.elapsedTime = this.stages
                            .slice(0, stageIndex)
                            .reduce((sum, s) => sum + s.duration, 0);
                        //this.updateStageProgress();
                        //this.updateProgressBar();
                        this.startIconRotation();
                    }
                });

                this.connection.start().catch((err) => console.error("SignalR Error:", err));
            },
            async loadDurations() {
                if (!this.vendorId) return;

                try {
                    const res = await fetch(`/publicvendorsettings/${this.vendorId}/status-durations`);
                    if (!res.ok) return;

                    const data = await res.json();

                    // Merge durations into the existing stage structure
                    for (const item of data) {
                        const stage = this.stages.find(s => s.status === item.status);
                        if (stage) {
                            console.log(stage)
                            stage.duration = Math.round(item.durationSeconds);
                            stage.remaining = Math.round(item.durationSeconds);
                            console.log(item.durationSeconds)
                            console.log(item.durationSeconds)

                        }
                    }
                } catch (err) {
                    console.error("Failed to load durations:", err);
                }
            },
            async checkOrderStatus() {
                if (!this.orderId) return;

                try {
                    const res = await fetchWithAuth(`/publicorder/get-user-order?id=${this.orderId}`);
                    if (!res.ok) {
                        console.error("Failed to fetch order status");
                        return;
                    }
                    const orderData = await res.json();
                    const order = orderData; 

                    this.collectionMode = order.collection;
                    if (this.collectionMode) {
                        this.stages = [
                            {
                                status: "confirmed",
                                label: "Waiting for Confirmation",
                                duration: 5,
                                remaining: 5,
                                color: "#3498db",
                                progressColor: "#2980b9",
                                icon: "/assets/tracker/confirm.gif"
                            },
                            {
                                status: "preparing",
                                label: "Preparing Food",
                                duration: 10,
                                remaining: 10,
                                color: "#f39c12",
                                progressColor: "#e67e22",
                                icon: "/assets/tracker/prep1.gif,/assets/tracker/prep2.gif,/assets/tracker/prep3.gif,/assets/tracker/prep4.gif"
                            },
                            {
                                status: "in_oven",
                                label: "In the Oven",
                                duration: 30,
                                remaining: 30,
                                color: "#e74c3c",
                                progressColor: "#c0392b",
                                icon: "/assets/tracker/cook1.gif,/assets/tracker/cook2.gif,/assets/tracker/cook3.gif,/assets/tracker/cook4.gif"
                            },
                            {
                                status: "ready_for_delivery",
                                label: "Ready to Collect", // Customized for collection
                                duration: 300,
                                remaining: 300,
                                color: "#27ae60",
                                progressColor: "#219150",
                                icon: "/assets/tracker/confirm.gif"
                            }
                        ]
                    }
                    else if (order.orderStatus == 'ready_for_delivery') {
                        order.orderStatus = 'out_for_delivery'; //doesnt matter too much here, on reload just set them to delivery, its waiting to go out for delivery
                    }

                    await this.loadDurations();

                    if (!order || !order.orderStatus) return;

                    const status = order.orderStatus.toLowerCase();

                    const stageIndex = this.stages.findIndex((s) =>
                        s.status.toLowerCase().includes(status)
                    );
                    console.log(this.stages)
                    console.log(status)
                    if (stageIndex !== -1) {
                        //this.barStarted = true;
                        console.log(status)
                        console.log(stageIndex)

                        this.currentStage = stageIndex;
                        this.elapsedTime = this.stages
                            .slice(0, stageIndex)
                            .reduce((sum, s) => sum + s.duration, 0);
                        this.startProgress();
                        //this.updateStageProgress();
                        //this.updateProgressBar();
                    }
                } catch (err) {
                    console.error("Error checking order status:", err);
                }
            },
            stageColor(label) {
                const map = {
                    "Waiting for Confirmation": "#3498db",
                    "Preparing Food": "#f39c12",
                    "In the Oven": "#e74c3c",
                    "Out for Delivery": "#8e44ad"
                };
                return map[label] || "#888";
            }

        },
    };
</script>

<style scoped>
    .order-tracker {
        padding: 20px;
        background-color: #25003e;
        color: #fff;
        border-radius: 10px;
        max-width: 800px;
        margin: 0 auto;
        text-align:center;
    }

    .progress-bar {
        position: relative;
        width: 100%;
        height: 14px;
        background-color: #2c2c2c;
        border-radius: 7px;
        overflow: hidden;
        margin-bottom: 20px;
    }

    .progress {
        height: 100%;
        background: linear-gradient(90deg, #a319d3, #e847ae);
        box-shadow: 0 0 10px #e847ae;
        transition: width 1s ease;
    }

    .tracker-icon {
        position: absolute;
        top: -24px;
        width: 24px;
        transition: left 1s ease;
    }

    .stages {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px;
        list-style: none;
        padding: 0;
        margin: 0;
    }

        .stages li {
            flex: 1;
            min-width: 120px;
            text-align: center;
            position: relative;
            padding: 10px;
            font-size: 14px;
            border-radius: 6px;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.05);
        }

            .stages li.active {
                color: #fff;
                font-weight: bold;
                box-shadow: inset 0 -2px 0 #e847ae;
            }

            .stages li.current {
                border: 1px solid #e847ae;
                background: rgba(255, 255, 255, 0.1);
            }

    .stage-title {
        display: block;
        margin-bottom: 4px;
    }

    .stage-time {
        font-size: 12px;
        color: #aaa;
    }

    @media (max-width: 600px) {
        .stages li {
            font-size: 12px;
            padding: 5px;
            min-width: 80px;
        }
    }


    .circular-progress-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        margin: 20px auto;
    }

    .circular-progress {
        width: 100%;
        height: 100%;
        transform: rotate(-90deg);
    }

    .icon-wrapper {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60px;
        height: 60px;
    }



        .icon-wrapper img {
            width: 100%;
            height: auto;
        }

    .arc-fill {
        transition: stroke-dasharray 0.5s ease-in-out, stroke-dashoffset 0.5s ease-in-out;
    }

    .icon-wrapper {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 24vw;
        height: 24vw;
        max-width: 300px;
        max-height: 300px;
    }

        .icon-wrapper img {
            width: 100%;
            height: auto;
        }

    @keyframes pulse {
        0% {
            stroke-width: 10px;
            opacity: 1;
        }

        50% {
            stroke-width: 12px;
            opacity: 0.8;
        }

        100% {
            stroke-width: 10px;
            opacity: 1;
        }
    }

    .arc-fill.active {
        animation: pulse 1.5s infinite ease-in-out;
    }

    @keyframes swing {
        0% {
            transform: rotate(0deg) translateY(0px);
        }

        25% {
            transform: rotate(5deg) translateY(2px);
        }

        50% {
            transform: rotate(0deg) translateY(-2px);
        }

        75% {
            transform: rotate(-5deg) translateY(2px);
        }

        100% {
            transform: rotate(0deg) translateY(0px);
        }
    }

    .icon-wrapper img {
        width: 100%;
        height: auto;
        animation: swing 2s infinite ease-in-out;
    }
</style>
